import { AnimatedContentLoader, Button, DropdownMenu, LucaIcon, Prompt } from '@components'
import { useBusinessContext } from '@context'
import { IStripeAddonInfo, useStripeAddons } from '@query'
import { MarkdownText } from '@root/containers/MarkdownText'
import { isLucaPaygAddon, LUCA_PAYG_ADDON_OFFERED } from '@utils/lucaPaygBilling'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaEllipsisV, FaTrash } from 'react-icons/fa'
import styled from 'styled-components'

export const AddonsSection: React.FC = () => {
  const { t } = useTranslation()
  const { slug, data } = useBusinessContext()
  const stripePaygCheckoutEnabled = Boolean(data?.stripe_payg_checkout_enabled)
  const [addonToRemove, setAddonToRemove] = useState<IStripeAddonInfo | null>(null)
  const {
    data: addonsData,
    isLoading,
    purchaseAddon,
    removeAddon,
    isPurchasing,
    isRemoving
  } = useStripeAddons({
    slug,
    enabled: stripePaygCheckoutEnabled
  })

  const availableAddons = useMemo(
    () =>
      addonsData?.addons.filter(
        addon =>
          addon.available &&
          (LUCA_PAYG_ADDON_OFFERED || !isLucaPaygAddon(addon.addon_id) || addon.active)
      ) ?? [],
    [addonsData?.addons]
  )
  const activeAddons = availableAddons.filter(addon => addon.active)
  const purchasableAddons = availableAddons.filter(addon => !addon.active)

  if (!stripePaygCheckoutEnabled) {
    return null
  }

  if (!isLoading && availableAddons.length === 0) {
    return null
  }

  const renderActiveAddonCard = (addon: IStripeAddonInfo) => {
    const menuItems = [
      <Button
        key="remove"
        type="button"
        intent="danger"
        icon={<FaTrash />}
        onClick={() => setAddonToRemove(addon)}
      >
        {t('settings.billing.addons.remove')}
      </Button>
    ]

    return (
      <AddonCard key={addon.addon_id} $isActive>
        <CardTop>
          <IconCol>
            <LucaIcon size={22} />
          </IconCol>
          <CardMain>
            <TitleRow>
              <TitleContent>
                <AddonTitle>{addon.name}</AddonTitle>
                <AddonDescription>
                  <MarkdownText text={addon.description} />
                </AddonDescription>
              </TitleContent>
              <MenuWrapper>
                <ActiveBadge>{t('settings.billing.addons.activeBadge')}</ActiveBadge>
                <DropdownMenu items={menuItems}>
                  <Button
                    type="button"
                    isSecondary
                    icon={<FaEllipsisV />}
                    aria-label={t('settings.billing.addons.actions')}
                    disabled={isRemoving}
                  />
                </DropdownMenu>
              </MenuWrapper>
            </TitleRow>
          </CardMain>
        </CardTop>
      </AddonCard>
    )
  }

  const renderAvailableAddonCard = (addon: IStripeAddonInfo) => (
    <AddonCard key={addon.addon_id}>
      <CardTop>
        <IconCol>
          <LucaIcon size={22} />
        </IconCol>
        <CardMain>
          <AddonTitle>{addon.name}</AddonTitle>
          <AddonDescription>
            <MarkdownText text={addon.description} />
          </AddonDescription>
        </CardMain>
      </CardTop>
      <CardActions>
        <BuyButton
          type="button"
          intent="primary"
          size="sm"
          showSpinner={isPurchasing}
          disabled={isPurchasing || isRemoving}
          onClick={() => purchaseAddon(addon.addon_id)}
        >
          {t('settings.billing.addons.buy')}
        </BuyButton>
      </CardActions>
    </AddonCard>
  )

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <Section>
        {activeAddons.length > 0 ? (
          <SubscriptionBlock>
            <BlockTitle>{t('settings.billing.addons.activeSectionTitle')}</BlockTitle>
            <CardList>{activeAddons.map(renderActiveAddonCard)}</CardList>
          </SubscriptionBlock>
        ) : null}

        {purchasableAddons.length > 0 ? (
          <SubscriptionBlock>
            <BlockTitle>{t('settings.billing.addons.availableSectionTitle')}</BlockTitle>
            <CardList>{purchasableAddons.map(renderAvailableAddonCard)}</CardList>
          </SubscriptionBlock>
        ) : null}

        <Prompt
          isVisible={Boolean(addonToRemove)}
          onClose={() => setAddonToRemove(null)}
          title={t('settings.billing.addons.removeConfirmTitle')}
          description={t('settings.billing.addons.removeConfirmDescription')}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setAddonToRemove(null)
            },
            {
              text: t('settings.billing.addons.remove'),
              intent: 'danger',
              showSpinner: isRemoving,
              disabled: isRemoving,
              action: () => {
                if (addonToRemove) {
                  removeAddon(addonToRemove.addon_id, {
                    onSuccess: () => setAddonToRemove(null)
                  })
                }
              }
            }
          ]}
        />
      </Section>
    </AnimatedContentLoader>
  )
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

const SubscriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const BlockTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const AddonCard = styled.div<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid
    ${({ $isActive, theme }) => ($isActive ? theme.luca.tintBorder : theme.luca.mistBorder)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.luca.mist};
  box-shadow: 0 1px 2px ${({ theme }) => theme.colors.metalGray}14;
`

const CardTop = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const IconCol = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 2.4rem;
  min-width: 2.4rem;
  height: 2.4rem;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  color: ${({ theme }) => theme.luca.blue};
`

const CardMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
`

const AddonTitle = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.luca.fontDisplay} !important;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.luca.navy};
`

const MenuWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-shrink: 0;
`

const ActiveBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.luca.navy};
  background: ${({ theme }) => theme.luca.tint};
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  white-space: nowrap;
`

const AddonDescription = styled.div`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.xs}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const BuyButton = styled(Button)`
  &.primary:not(.is-secondary) {
    background: ${({ theme }) => theme.luca.navy};
    color: ${({ theme }) => theme.colors.neutralWhite};
  }
`
